<template>
  <section class="admin-content" id="qa-dashboard">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="fe fe-activity" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">M &amp; E Encounter Dashboard</h4>
                <small style="opacity: 0.8;">Form-specific analytics with configurable claims and referrals
                  metrics</small>
              </div>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <TodayTasks />
              <router-link to="/state-dashboard" class="btn filter-toggle-btn">
                <i class="fe fe-home mr-1"></i> State Dashboard
              </router-link>
              <button class="btn filter-toggle-btn" @click="narrativeOpen = true" title="Write a Word or PDF report of the agency's data">
                <i class="fe fe-file-text mr-1"></i> Narrative report
              </button>
              <button class="btn filter-toggle-btn" :class="{ active: aggregateOpen }"
                @click="aggregateOpen = !aggregateOpen">
                <i class="fe mr-1" :class="aggregateOpen ? 'fe-x' : 'fe-grid'"></i>
                {{ aggregateOpen ? 'Close Aggregate' : 'Aggregate' }}
              </button>
              <button class="btn filter-toggle-btn" :class="{ active: filterOpen }" @click="filterOpen = !filterOpen">
                <i class="fe mr-1" :class="filterOpen ? 'fe-x' : 'fe-filter'"></i>
                {{ filterOpen ? 'Close Filter' : 'Filter' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <!-- Filter & Actions Card -->
          <div v-show="filterOpen" class="card shadow-sm form-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-sliders" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Filter Parameters</h6>
              <div class="ml-auto d-flex align-items-center" style="gap: 6px;">
                <button class="btn-header-action" @click="exportAllCsvZip">
                  <i class="fe fe-package mr-1"></i> Export ZIP
                </button>
                <!-- <button class="btn-header-action" @click="exportDashboardSummary">
                  <i class="fe fe-file-text mr-1"></i> Summary
                </button> -->
                <!-- <button class="btn-header-action" @click="exportKpiCsv">
                  <i class="fe fe-download mr-1"></i> KPI CSV
                </button> -->
              </div>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row">
                <div class="form-group col-md-3">
                  <label class="filter-label">Select Form</label>
                  <select class="form-control" v-model="query.form_id" @change="onFormChange">
                    <option :value="null">Select form</option>
                    <option v-for="form in forms" :key="form.id" :value="form.id">{{ form.name }}</option>
                  </select>
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label">LGA</label>
                  <select class="form-control" v-model="query.localgovt">
                    <option :value="null">All LGAs</option>
                    <option v-for="lga in lga_states" :key="lga.id" :value="lga.id">{{ lga.local_name }}</option>
                  </select>
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label">Care Level</label>
                  <select class="form-control" v-model="query.phc_type" @change="onProviderFilterChange">
                    <option :value="null">All</option>
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="tertiary">Tertiary</option>
                  </select>
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label">Facility Type</label>
                  <select class="form-control" v-model="query.phc_general" @change="onProviderFilterChange">
                    <option :value="null">All</option>
                    <option>Public</option>
                    <option>Private</option>
                  </select>
                </div>
                <div class="form-group col-md-6">
                  <label class="filter-label">Facility / Provider</label>
                  <v-select v-model="selectedProviderId" :options="providers" label="agency_name" multiple
                    :reduce="p => p.id" placeholder="All facilities…" />
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label"><i class="fe fe-calendar mr-1"></i>Start Date</label>
                  <input type="date" class="form-control" v-model="query.from" />
                </div>
                <div class="form-group col-md-3">
                  <label class="filter-label"><i class="fe fe-calendar mr-1"></i>End Date</label>
                  <input type="date" class="form-control" v-model="query.to" />
                </div>
              </div>

              <div class="filter-divider"><span>Item Response Analysis</span></div>

              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="filter-label">Select Item to Analyse</label>
                  <v-select v-model="selectedAnalysisItemId" :options="qaItems" label="item_name"
                    :reduce="item => item.id" placeholder="— Pick an item —" />
                </div>
                <div class="form-group col-md-3 d-flex align-items-end">
                  <button class="btn btn-outline-info btn-block" @click="fetchItemAnalysis"
                    :disabled="!selectedAnalysisItemId || isLoading">
                    <i class="fe fe-bar-chart-2 mr-1"></i> Load Chart
                  </button>
                </div>
                <div class="form-group col-md-3 d-flex align-items-end">
                  <button class="btn btn-submit btn-block" @click="fetchAnalytics()" :disabled="isLoading">
                    <i class="fe fe-refresh-cw mr-1"></i> Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Aggregate Report Parameters -->
          <div v-show="aggregateOpen" class="card shadow-sm form-card mb-4" id="aggregate-report">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-grid" style="font-size:12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Aggregate Report Parameters</h6>
              <small class="ml-2" style="color: rgba(255,255,255,.75); font-size:0.75rem;">
                Totals of numeric items per State / LGA / period
              </small>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="filter-label">Form <span class="text-danger">*</span></label>
                  <select class="form-control" v-model="reportQuery.form_id" @change="report = null">
                    <option :value="null">Select form</option>
                    <option v-for="form in forms" :key="`rf-${form.id}`" :value="form.id">{{ form.name }}</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="filter-label">LGA</label>
                  <select class="form-control" v-model="reportQuery.localgovt" @change="onReportLgaChange">
                    <option :value="null">All LGAs</option>
                    <option v-for="lga in lga_states" :key="`rl-${lga.id}`" :value="lga.id">{{ lga.local_name }}
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="filter-label">Facility / Provider</label>
                  <v-select v-model="reportQuery.provider_ids" :options="reportProviders" label="agency_name" multiple
                    :reduce="p => p.id" placeholder="All facilities…" />
                </div>
                <div class="form-group col-md-4">
                  <label class="filter-label">Group By</label>
                  <select class="form-control" v-model="reportQuery.period">
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                    <option value="year">Year</option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="filter-label"><i class="fe fe-calendar mr-1"></i>From</label>
                  <input type="date" class="form-control" v-model="reportQuery.from" />
                </div>
                <div class="form-group col-md-4">
                  <label class="filter-label"><i class="fe fe-calendar mr-1"></i>To</label>
                  <input type="date" class="form-control" v-model="reportQuery.to" :min="reportQuery.from || null" />
                </div>
              </div>

              <div class="d-flex justify-content-end flex-wrap" style="gap: 8px;">
                <button class="btn btn-outline-success" @click="downloadReportExcel"
                  :disabled="!reportQuery.form_id || reportLoading">
                  <i class="fe fe-download mr-1"></i> Export Excel
                </button>
                <button class="btn btn-submit" @click="generateReport"
                  :disabled="!reportQuery.form_id || reportLoading">
                  <span v-if="reportLoading" class="spinner-border spinner-border-sm mr-1"></span>
                  <i v-else class="fe fe-play mr-1"></i> Generate Report
                </button>
              </div>
            </div>
          </div>

          <!-- Aggregate Report Results -->
          <div v-if="report" class="card shadow-sm table-card mb-4" ref="reportResults">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-table" style="font-size:12px;"></i></div>
              <div>
                <h6 class="mb-0 font-weight-bold text-white">Aggregate Report — {{ report.form.name }}</h6>
                <small style="opacity:.75; font-size:0.75rem; color:#fff;">
                  {{ reportPeriodLabel }} · {{ report.rows.length }} row{{ report.rows.length === 1 ? '' : 's' }}
                  · {{ report.columns.length }} numeric item{{ report.columns.length === 1 ? '' : 's' }}
                </small>
              </div>
              <div class="ml-auto d-flex align-items-center" style="gap: 6px;">
                <button class="btn-header-action" @click="downloadReportExcel" :disabled="reportLoading">
                  <i class="fe fe-download mr-1"></i> Export Excel
                </button>
                <button class="btn-header-icon" title="Close report" @click="report = null">
                  <i class="fe fe-x"></i>
                </button>
              </div>
            </div>
            <div class="card-body p-3">
              <div v-if="!report.rows.length" class="empty-state">
                <i class="fe fe-inbox"></i>
                <p>No numeric responses found for the selected filters.</p>
              </div>

              <div v-else class="report-table-wrap">
                <table class="table table-sm table-bordered report-table mb-0">
                  <thead>
                    <tr>
                      <th class="sticky-col">State</th>
                      <th class="sticky-col sticky-col-2">LGA</th>
                      <th>Year</th>
                      <th v-if="report.filters.period !== 'year'">Quarter</th>
                      <th v-if="report.filters.period === 'month'">Month</th>
                      <th v-for="col in report.columns" :key="`rc-${col.item_id}`" class="item-col" :title="col.label">
                        {{ col.label }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in report.rows" :key="`rr-${idx}`">
                      <td class="sticky-col">{{ row.state }}</td>
                      <td class="sticky-col sticky-col-2">{{ row.lga }}</td>
                      <td>{{ row.year }}</td>
                      <td v-if="report.filters.period !== 'year'">{{ row.quarter }}</td>
                      <td v-if="report.filters.period === 'month'">{{ row.month }}</td>
                      <td v-for="col in report.columns" :key="`rv-${idx}-${col.item_id}`" class="text-right">
                        {{ formatNumber(row.values[col.item_id]) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <th class="sticky-col">Total</th>
                      <th class="sticky-col sticky-col-2"></th>
                      <th :colspan="reportFixedColumnCount - 2"></th>
                      <th v-for="col in report.columns" :key="`rt-${col.item_id}`" class="text-right">
                        {{ formatNumber(report.totals[col.item_id]) }}
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <!-- Summary Stat Cards -->
          <div class="row mb-4">
            <div class="col-6 col-md-4 col-lg-3" v-for="card in summaryCards" :key="card.key">
              <div class="stat-card">
                <div class="stat-icon-wrap" :style="{ background: card.iconBg }">
                  <i :class="card.icon" :style="{ color: card.iconColor }"></i>
                </div>
                <div class="stat-body">
                  <p class="stat-label">{{ card.title }}</p>
                  <h3 class="stat-value">{{ card.value }}</h3>
                  <a v-if="card.link" :href="card.link" target="_blank" class="stat-link">
                    <i class="fe fe-external-link mr-1"></i>{{ card.linkText }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Facilities that did not submit -->
          <div v-if="analytics && facilitiesNotSubmitted.length" class="card shadow-sm mb-4 non-submit-card">
            <div class="card-header d-flex justify-content-between align-items-center non-submit-header">
              <div>
                <h6 class="m-0 font-weight-bold" style="color: #92400e;">
                  <i class="fe fe-alert-triangle mr-2" style="color: #f59e0b;"></i>Facilities That Did Not Submit
                </h6>
                <small class="text-muted">
                  <strong style="color: #b45309;">{{ facilitiesNotSubmitted.length }}</strong>
                  {{ facilitiesNotSubmitted.length === 1 ? 'facility has' : 'facilities have' }} not submitted for the
                  selected form and period
                </small>
              </div>
              <div class="d-flex align-items-center" style="gap: 8px;">
                <button class="btn btn-sm btn-outline-secondary" @click="notSubmittedOpen = !notSubmittedOpen">
                  <i class="fe mr-1" :class="notSubmittedOpen ? 'fe-chevron-up' : 'fe-chevron-down'"></i>
                  {{ notSubmittedOpen ? 'Hide' : 'Show' }} List
                </button>
                <button class="btn btn-sm btn-outline-warning" @click="exportNonSubmittersCsv">
                  <i class="fe fe-download mr-1"></i> Export CSV
                </button>
              </div>
            </div>
            <div v-show="notSubmittedOpen" class="card-body">
              <div class="d-flex flex-wrap" style="gap: 8px;">
                <span v-for="p in facilitiesNotSubmitted" :key="p.id" class="facility-chip">
                  {{ p.agency_name }}
                </span>
              </div>
            </div>
          </div>

          <!-- Charts Row 1: Facility Entries + Item Numeric Totals -->
          <div class="row">
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-home" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Facility / Entries</h6>
                  <span v-if="facilityEntries.length" class="badge badge-light ml-2" style="border-radius:20px;">{{
                    facilityEntries.length }}</span>
                  <button class="btn-header-icon ml-auto" title="Export CSV"
                    @click="exportChartCsv('facility_entries', facilityEntries)">
                    <i class="fe fe-download"></i>
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="facilityEntries.length === 0" class="empty-state">
                    <i class="fe fe-inbox"></i>
                    <p>No data — apply filters and click Apply Filters.</p>
                  </div>
                  <div v-for="entry in facilityEntries" :key="`f-e-${entry.facility_id}`" class="bar-row">
                    <div class="bar-label">{{ entry.facility_name }}</div>
                    <!-- <div class="bar-track">
                      <div class="bar-fill" style="background: #2563eb;" :style="barWidth(entry.total_entries, maxFacilityEntries)"></div>
                    </div> -->
                    <!-- <div class="bar-value">{{ entry.total_entries }}</div> -->
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-hash" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Item Numeric Totals</h6>
                  <span v-if="itemNumericTotals.length" class="badge badge-light ml-2" style="border-radius:20px;">{{
                    itemNumericTotals.length }}</span>
                  <button class="btn-header-icon ml-auto" title="Export CSV"
                    @click="exportChartCsv('item_numeric_totals', itemNumericTotals)">
                    <i class="fe fe-download"></i>
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="itemNumericTotals.length === 0" class="empty-state">
                    <i class="fe fe-inbox"></i>
                    <p>No data — apply filters and click Apply Filters.</p>
                  </div>
                  <div v-for="item in itemNumericTotals" :key="`sum-${item.item_id}`" class="bar-row">
                    <div class="bar-label bar-label--full">{{ item.item_name }}</div>
                    <div class="bar-track">
                      <div class="bar-fill" style="background: #e11d48;"
                        :style="barWidth(item.numeric_sum, maxItemNumericTotals)"></div>
                    </div>
                    <div class="bar-value">{{ formatNumber(item.numeric_sum) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Charts Row 2: Encounter Trend + Item Utilization -->
          <div class="row">
            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-trending-up" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Encounter Trend</h6>
                  <span v-if="encounterTrend.length" class="badge badge-light ml-2" style="border-radius:20px;">{{
                    encounterTrend.length }}</span>
                  <button class="btn-header-icon ml-auto" title="Export CSV"
                    @click="exportChartCsv('encounter_trend', encounterTrend)">
                    <i class="fe fe-download"></i>
                  </button>
                </div>
                <div class="card-body p-3">
                  <encounters-by-date-chart
                    :by-date="encounterTrend.map(function(p) { return { date: p.period, total: p.total, claimed: p.claimed || 0 }; })"
                    :loading="isLoading" filename="encounter-trend" />
                </div>
              </div>
            </div>

            <div class="col-lg-6 mb-4">
              <div class="card shadow-sm table-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-bar-chart-2" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Item Utilization</h6>
                  <span v-if="itemUtilization.length" class="badge badge-light ml-2" style="border-radius:20px;">{{
                    itemUtilization.length }}</span>
                  <button class="btn-header-icon ml-auto" title="Export CSV"
                    @click="exportChartCsv('item_utilization', itemUtilization)">
                    <i class="fe fe-download"></i>
                  </button>
                </div>
                <div class="card-body">
                  <div v-if="itemUtilization.length === 0" class="empty-state">
                    <i class="fe fe-inbox"></i>
                    <p>No data — apply filters and click Apply Filters.</p>
                  </div>
                  <div v-for="item in itemUtilization" :key="`util-${item.item_id}`" class="bar-row">
                    <div class="bar-label bar-label--full">{{ item.item_name }}</div>
                    <div class="bar-track">
                      <div class="bar-fill" style="background: #d97706;"
                        :style="barWidth(item.responses_count, maxItemUtilization)"></div>
                    </div>
                    <div class="bar-value">{{ item.responses_count }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Item Response Analysis Chart -->
          <div v-if="itemAnalysis" class="card shadow-sm table-card mb-4">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-pie-chart" style="font-size:12px;"></i></div>
              <div>
                <h6 class="mb-0 font-weight-bold text-white">
                  Response Analysis — <em style="font-weight:400;">{{ itemAnalysis.item.item_name }}</em>
                </h6>
                <small style="opacity:.75; font-size:0.75rem;">
                  {{ itemAnalysis.from }} → {{ itemAnalysis.to }} · {{ itemAnalysis.total_response_rows }} response rows
                  <span v-if="itemAnalysis.item.is_multi_select" class="badge badge-light ml-1">multi-select</span>
                  <span class="badge badge-light ml-1">{{ itemAnalysis.item.response_type }}</span>
                </small>
              </div>
            </div>
            <div class="card-body p-3">
              <div v-if="!itemAnalysis.data.length" class="empty-state">
                <i class="fe fe-inbox"></i>
                <p>No responses found for this item in the selected date range.</p>
              </div>
              <apexchart v-else type="bar" height="380" :options="itemAnalysisChartOptions"
                :series="itemAnalysisChartSeries" />
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="false" :is-full-page="fullPage" />
        </div>
      </section>
    <NarrativeReportModal v-model="narrativeOpen" />
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import TodayTasks from "@/components/TodayTasks.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import EncountersByDateChart from "@/views/shis/components/EncountersByDateChart.vue";
  import moment from "moment";
  import NarrativeReportModal from "@/views/reports/components/NarrativeReportModal.vue";

  moment().format();

  export default {
    components: { TodayTasks,
      NarrativeReportModal,
      Navbar,
      Loading,
      EncountersByDateChart,
    },
    data() {
      return {
        narrativeOpen: false,
        isLoading: false,
        fullPage: true,
        user: null,
        filterOpen: false,
        notSubmittedOpen: false,
        forms: [],
        encounterSummary: null,
        claimsSummary: null,
        referralSummary: null,
        qaItems: [],
        analytics: null,
        lga_states: [],
        providers: [],
        selectedProviderId: null,
        selectedAnalysisItemId: null,
        itemAnalysis: null,
        aggregateOpen: false,
        report: null,
        reportLoading: false,
        reportProviderList: [],
        reportQuery: {
          form_id: null,
          localgovt: null,
          provider_ids: [],
          period: "month",
          from: moment().subtract(30, "days").format("YYYY-MM-DD"),
          to: moment().format("YYYY-MM-DD"),
        },
        query: {
          agency_id: null,
          form_id: 21,
          localgovt: null,
          provider_ids: [],
          phc_type: "primary",
          phc_general: null,
          claims_item_id: null,
          referrals_item_id: null,
          from: moment().subtract(30, "days").format("YYYY-MM-DD"),
          to: moment().format("YYYY-MM-DD"),
        },
      };
    },
    computed: {
      cards() {
        return this.analytics && this.analytics.cards
          ? this.analytics.cards
          : {
            total_encounters: 0,
            sum_of_claims: 0,
            number_of_facility: 0,
            number_of_referrals: 0,
          };
      },
      summaryCards() {
        const enc = this.encounterSummary || {};
        const ref = this.referralSummary || {};
        const claimsArr = Array.isArray(this.claimsSummary) ? this.claimsSummary : [];
        const totalClaimsCount = claimsArr.reduce(function (s, p) { return s + (Number(p.claims_count) || 0); }, 0);
        const totalClaimsAmount = claimsArr.reduce(function (s, p) { return s + (Number(p.total_cost) || 0); }, 0);
        return [
          {
            key: "number_of_facility",
            title: "Facility Submissions",
            icon: "fe fe-home",
            iconBg: "#e2e8f0",
            iconColor: "#334155",
            value: this.formatNumber(this.facilityEntries.length || this.cards.number_of_facility || this.cards.number_of_facilities || 0),
            link: "/quality-assurance",
            linkText: "View M&Es",
          },
          {
            key: "total_encounters",
            title: "Total Encounters",
            icon: "mdi mdi-file-document-outline",
            iconBg: "#e8f4ff",
            iconColor: "#2563eb",
            value: this.formatNumber(enc.total || this.cards.total_encounters || 0),
            link: "/encounter-statistics",
            linkText: "View Encounters",
          },
          {
            key: "sum_of_claims",
            title: "Total Claims",
            icon: "fe fe-credit-card",
            iconBg: "#f1f5f9",
            iconColor: "#475569",
            value: this.formatNumber(totalClaimsCount || this.cards.sum_of_claims || 0) + " (" + this.formatCurrency(totalClaimsAmount) + ")",
            link: "/claims-statistics",
            linkText: "View Claims",
          },
          {
            key: "number_of_referrals",
            title: "Total Referrals",
            icon: "fe fe-share-2",
            iconBg: "#eff6ff",
            iconColor: "#3b82f6",
            value: this.formatNumber(this.cards.number_of_referrals || ref.total || 0),
            link: "/authorization-code-statistics",
            linkText: "View Referrals",
          },
          {
            key: "facilities_not_submitted",
            title: "Did Not Submit",
            icon: "fe fe-alert-triangle",
            iconBg: "#fef3c7",
            iconColor: "#d97706",
            value: this.analytics ? this.formatNumber(this.facilitiesNotSubmitted.length) : "—",
            link: null,
            linkText: null,
          },
        ];
      },
      charts() {
        return this.analytics && this.analytics.charts ? this.analytics.charts : {};
      },
      facilityEntries() {
        return this.charts.facility_entries || [];
      },
      facilityReferrals() {
        return this.charts.facility_referrals || [];
      },
      encounterTrend() {
        return this.charts.encounter_trend || [];
      },
      itemUtilization() {
        return this.charts.item_utilization || [];
      },
      itemNumericTotals() {
        return this.charts.item_numeric_totals || [];
      },
      topResponseOptions() {
        return this.charts.top_response_options || [];
      },
      facilitiesNotSubmitted() {
        if (!this.providers.length) return [];
        const scopedIds = new Set(
          Array.isArray(this.query.provider_ids) && this.query.provider_ids.length
            ? this.query.provider_ids.map(String)
            : this.providers.map(function (p) { return String(p.id); })
        );
        const submittedIds = new Set(this.facilityEntries.map(function (e) { return String(e.facility_id); }));
        return this.providers.filter(function (p) {
          return scopedIds.has(String(p.id)) && !submittedIds.has(String(p.id));
        });
      },
      maxFacilityEntries() {
        return this.getMax(this.facilityEntries, "total_entries");
      },
      maxFacilityReferrals() {
        return this.getMax(this.facilityReferrals, "referrals");
      },
      maxEncounterTrend() {
        return this.getMax(this.encounterTrend, "total");
      },
      maxItemUtilization() {
        return this.getMax(this.itemUtilization, "responses_count");
      },
      maxItemNumericTotals() {
        return this.getMax(this.itemNumericTotals, "numeric_sum");
      },
      itemAnalysisChartOptions() {
        if (!this.itemAnalysis || !this.itemAnalysis.data.length) {
          return {};
        }
        return {
          chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
          plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "60%" } },
          dataLabels: { enabled: true },
          xaxis: {
            categories: this.itemAnalysis.data.map(function (d) { return d.label; }),
            labels: { style: { fontSize: "11px", colors: "#64748b" } },
          },
          colors: ["#2563eb"],
          grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
          tooltip: {
            y: { formatter: function (v) { return v.toLocaleString() + " responses"; } },
          },
        };
      },
      reportProviders() {
        const lga = this.reportQuery.localgovt;
        if (!lga) return this.reportProviderList;
        return this.reportProviderList.filter((p) => {
          const providerLga = p.localgovt && typeof p.localgovt === "object" ? p.localgovt.id : p.localgovt;
          return String(providerLga) === String(lga);
        });
      },
      reportFixedColumnCount() {
        const period = this.report ? this.report.filters.period : "month";
        return period === "month" ? 5 : period === "quarter" ? 4 : 3;
      },
      reportPeriodLabel() {
        if (!this.report) return "";
        const f = this.report.filters;
        const range = f.from || f.to ? `${f.from || "start"} → ${f.to || "today"}` : "All time";
        return `By ${f.period} · ${range}`;
      },
      itemAnalysisChartSeries() {
        if (!this.itemAnalysis || !this.itemAnalysis.data.length) {
          return [];
        }
        return [{
          name: "Responses",
          data: this.itemAnalysis.data.map(function (d) { return d.count; }),
        }];
      },
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.query.agency_id = this.resolveAgencyId();
    },
    methods: {
      resolveAgencyId() {
        return (
          (this.user && this.user.institutional_id) ||
          (this.user && this.user.agency_id) ||
          407465
        );
      },
      getMax(list, field) {
        if (!list || list.length === 0) {
          return 1;
        }
        const max = Math.max(...list.map((item) => Number(item[field]) || 0));
        return max > 0 ? max : 1;
      },
      barWidth(value, max) {
        const width = ((Number(value) || 0) / max) * 100;
        return { width: `${Math.min(100, Math.max(0, width))}%` };
      },
      formatNumber(value) {
        const parsed = Number(value) || 0;
        return parsed.toLocaleString();
      },
      formatCurrency(value) {
        const parsed = Number(value) || 0;
        return `N${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
      },
      buildFilenamePrefix() {
        const form = this.forms.find((item) => Number(item.id) === Number(this.query.form_id));
        const formName = form && form.name ? form.name : "form";
        const safeFormName = String(formName)
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        return `qa-dashboard-${safeFormName || "form"}-${this.query.from}-to-${this.query.to}`;
      },
      escapeCsvCell(value) {
        const text = value === null || value === undefined ? "" : String(value);
        if (text.includes('"') || text.includes(",") || text.includes("\n")) {
          return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
      },
      rowsToCsv(rows) {
        if (!rows || rows.length === 0) { return ""; }
        const headers = Object.keys(rows[0]);
        const lines = [headers.join(",")];
        rows.forEach((row) => {
          const cells = headers.map((header) => this.escapeCsvCell(row[header]));
          lines.push(cells.join(","));
        });
        return lines.join("\n");
      },
      downloadTextFile(filename, content, mimeType = "text/plain;charset=utf-8;") {
        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      exportChartCsv(chartName, rows) {
        if (!rows || rows.length === 0) {
          this.$toasted.info("No data to export for this chart", { position: "top-center", duration: 2000 });
          return;
        }
        const csv = this.rowsToCsv(rows);
        const filename = `${this.buildFilenamePrefix()}-${chartName}.csv`;
        this.downloadTextFile(filename, csv, "text/csv;charset=utf-8;");
      },
      exportKpiCsv() {
        const row = {
          total_encounters: this.cards.total_encounters || 0,
          sum_of_claims: this.cards.sum_of_claims || 0,
          number_of_facility: this.cards.number_of_facility || this.cards.number_of_facilities || 0,
          number_of_referrals: this.cards.number_of_referrals || 0,
        };
        const csv = this.rowsToCsv([row]);
        const filename = `${this.buildFilenamePrefix()}-kpi.csv`;
        this.downloadTextFile(filename, csv, "text/csv;charset=utf-8;");
      },
      exportDashboardSummary() {
        const summaryLines = [
          "M & E Dashboard Summary",
          `Agency ID: ${this.query.agency_id || ""}`,
          `Form ID: ${this.query.form_id || ""}`,
          `Date Range: ${this.query.from} to ${this.query.to}`,
          `LGA: ${this.query.localgovt || "All"}`,
          `Provider IDs: ${this.query.provider_ids && this.query.provider_ids.length > 0 ? this.query.provider_ids.join(",") : "All"}`,
          "",
          `Total Encounters: ${this.formatNumber(this.cards.total_encounters)}`,
          `Sum of Claims: ${this.formatCurrency(this.cards.sum_of_claims)}`,
          `Number of Facility: ${this.formatNumber(this.cards.number_of_facility || this.cards.number_of_facilities)}`,
          `Number of Referrals: ${this.formatNumber(this.cards.number_of_referrals)}`,
          "",
          `Facility Entries Rows: ${this.facilityEntries.length}`,
          `Facility Referrals Rows: ${this.facilityReferrals.length}`,
          `Encounter Trend Rows: ${this.encounterTrend.length}`,
          `Item Utilization Rows: ${this.itemUtilization.length}`,
          `Item Numeric Totals Rows: ${this.itemNumericTotals.length}`,
          `Top Response Options Rows: ${this.topResponseOptions.length}`,
        ];
        const filename = `${this.buildFilenamePrefix()}-summary.txt`;
        this.downloadTextFile(filename, summaryLines.join("\n"));
      },
      async exportAllCsvZip() {
        if (!this.query.form_id) {
          this.$toasted.error("Please select a form", { position: "top-center", duration: 2500 });
          return;
        }
        this.isLoading = true;
        try {
          const payload = {
            agency_id: this.query.agency_id,
            form_id: this.query.form_id,
            from: this.query.from,
            to: this.query.to,
            localgovt: this.query.localgovt,
            provider_ids: this.query.provider_ids,
            phc_type: this.query.phc_type,
            phc_general: this.query.phc_general,
            claims_item_id: this.query.claims_item_id,
            referrals_item_id: this.query.referrals_item_id,
          };
          Object.keys(payload).forEach((key) => {
            if (payload[key] === null || payload[key] === "") { delete payload[key]; }
          });
          const response = await this.axios.post(`/api/v1/auth/qa-dashboard/export-all`, payload, { responseType: "blob" });
          const contentDisposition = response.headers["content-disposition"] || "";
          const matched = contentDisposition.match(/filename="?([^"]+)"?/i);
          const fallbackName = `${this.buildFilenamePrefix()}-export.zip`;
          const filename = matched && matched[1] ? matched[1] : fallbackName;
          const blob = new Blob([response.data], { type: "application/zip" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.$toasted.success("ZIP export downloaded", { position: "top-center", duration: 2500 });
        } catch (error) {
          console.error(error);
          this.$toasted.error("Failed to export ZIP", { position: "top-center", duration: 3000 });
        } finally {
          this.isLoading = false;
        }
      },
      async fetchItemAnalysis() {
        if (!this.selectedAnalysisItemId) { return; }
        this.isLoading = true;
        try {
          const { data } = await this.axios.post("/api/v1/auth/qa-dashboard/item-responses", {
            item_id: this.selectedAnalysisItemId,
            agency_id: this.query.agency_id,
            from: this.query.from,
            to: this.query.to,
          });
          this.itemAnalysis = data;
        } catch (error) {
          console.error(error);
          this.$toasted.error("Failed to load response analysis", { position: "top-center", duration: 3000 });
        } finally {
          this.isLoading = false;
        }
      },
      async fetchForms() {
        const { data } = await this.axios.get(`/api/v1/auth/forms/407465`);
        this.forms = Array.isArray(data) ? data : [];
      },
      async fetchMeta() {
        if (!this.query.agency_id) { return; }
        const params = { agency_id: this.query.agency_id };
        if (this.query.form_id) { params.form_id = this.query.form_id; }
        const { data } = await this.axios.get(`/api/v1/auth/qa-dashboard/meta`, { params });
        this.qaItems = data.items || [];
        if (!this.query.form_id && data.selected_form_id) {
          this.query.form_id = data.selected_form_id;
        }
        if (!this.query.claims_item_id && data.config) {
          this.query.claims_item_id = data.config.claims_item_id;
        }
        if (!this.query.referrals_item_id && data.config) {
          this.query.referrals_item_id = data.config.referrals_item_id;
        }
      },
      async fetchAnalytics(showToast = true) {
        if (!this.query.form_id) {
          this.$toasted.error("Please select a form", { position: "top-center", duration: 3000 });
          return;
        }
        this.isLoading = true;
        try {
          const payload = {
            agency_id: this.query.agency_id,
            form_id: this.query.form_id,
            from: this.query.from,
            to: this.query.to,
            localgovt: this.query.localgovt,
            provider_ids: this.query.provider_ids,
            phc_type: this.query.phc_type,
            phc_general: this.query.phc_general,
            claims_item_id: this.query.claims_item_id,
            referrals_item_id: this.query.referrals_item_id,
          };
          Object.keys(payload).forEach((key) => {
            if (payload[key] === null || payload[key] === "") { delete payload[key]; }
          });
          const { data } = await this.axios.post(`/api/v1/auth/qa-dashboard/analytics`, payload);
          this.analytics = data;

          await Promise.all([
            this.fetchEncounterStats(),
            this.fetchClaimsStats(),
            this.fetchReferralStats(),
            this.fetchProviders(),
          ]);

          if (showToast) {
            this.$toasted.info("Dashboard refreshed", { position: "top-center", duration: 2500 });
          }
        } catch (error) {
          console.error(error);
          this.$toasted.error("Unable to fetch dashboard analytics", { position: "top-center", duration: 3000 });
        } finally {
          this.isLoading = false;
        }
      },
      async saveMetricConfig() {
        if (!this.query.form_id) {
          this.$toasted.error("Please select a form first", { position: "top-center", duration: 2500 });
          return;
        }
        this.isLoading = true;
        try {
          await this.axios.post(`/api/v1/auth/qa-dashboard/config`, {
            agency_id: this.query.agency_id,
            form_id: this.query.form_id,
            claims_item_id: this.query.claims_item_id,
            referrals_item_id: this.query.referrals_item_id,
          });
          this.$toasted.success("Metric mapping saved", { position: "top-center", duration: 2500 });
        } catch (error) {
          console.error(error);
          this.$toasted.error("Failed to save metric mapping", { position: "top-center", duration: 3000 });
        } finally {
          this.isLoading = false;
        }
      },
      async fetchEncounterStats() {
        try {
          const payload = { agency_id: this.query.agency_id, from: this.query.from, to: this.query.to };
          const ids = Array.isArray(this.query.provider_ids) ? this.query.provider_ids : [];
          if (ids.length === 1) payload.provider_id = ids[0];
          const { data } = await this.axios.post("/api/v1/auth/encounter/statistics", payload);
          this.encounterSummary = data.summary || null;
        } catch (e) { console.error(e); }
      },
      async fetchClaimsStats() {
        try {
          const { data } = await this.axios.post("/api/v1/auth/claims/statistics", {
            agency_id: this.query.agency_id,
            from: this.query.from,
            to: this.query.to,
            sector: this.query.phc_general || undefined,
          });
          this.claimsSummary = Array.isArray(data.data) ? data.data : [];
        } catch (e) { console.error(e); }
      },
      async fetchReferralStats() {
        try {
          const { data } = await this.axios.post("/api/v1/auth/authorization-code/statistics", {
            agency_id: this.query.agency_id,
            from: this.query.from,
            to: this.query.to,
          });
          this.referralSummary = data.summary || null;
        } catch (e) { console.error(e); }
      },
      exportNonSubmittersCsv() {
        const list = this.facilitiesNotSubmitted;
        if (!list.length) {
          this.$toasted.info("No non-submitting facilities to export", { position: "top-center", duration: 2000 });
          return;
        }
        const rows = list.map(function (p) {
          return { facility_name: p.agency_name, facility_code: p.facility_code || "", lga: p.localgovt || "", ward: p.ward || "" };
        });
        const csv = this.rowsToCsv(rows);
        this.downloadTextFile(this.buildFilenamePrefix() + "-did-not-submit.csv", csv, "text/csv;charset=utf-8;");
      },
      async onFormChange() {
        this.query.claims_item_id = null;
        this.query.referrals_item_id = null;
        this.selectedAnalysisItemId = null;
        this.itemAnalysis = null;
        await this.fetchMeta();
        await this.fetchAnalytics(false);
      },
      async fetchProviders() {
        if (!this.query.agency_id) { return; }
        try {
          const params = { phc_type: this.query.phc_type, phc_general: this.query.phc_general };
          const { data } = await this.axios.get(`/api/v1/auth/providerAgency/${this.query.agency_id}`, { params });
          this.providers = Array.isArray(data.data) ? data.data : [];
          if (this.selectedProviderId) {
            const selectedStillExists = this.providers.some((provider) => provider.id === this.selectedProviderId);
            if (!selectedStillExists) { this.selectedProviderId = null; }
          }
          this.query.provider_ids = this.selectedProviderId
            ? [this.selectedProviderId]
            : this.providers.map((provider) => provider.id);
        } catch (error) { console.error(error); }
      },
      async onProviderFilterChange() {
        await this.fetchProviders();
      },
      onSelectedProviderChange() {
        this.query.provider_ids = this.selectedProviderId
          ? [this.selectedProviderId]
          : this.providers.map((provider) => provider.id);
      },
      buildReportPayload(format) {
        const q = this.reportQuery;
        const payload = {
          agency_id: this.query.agency_id,
          form_id: q.form_id,
          period: q.period,
          format,
        };
        if (q.localgovt) payload.localgovt = q.localgovt;
        if (q.provider_ids && q.provider_ids.length) payload.provider_ids = q.provider_ids;
        if (q.from) payload.from = q.from;
        if (q.to) payload.to = q.to;
        return payload;
      },
      validateReportQuery() {
        if (!this.reportQuery.form_id) {
          this.$toasted.error("Please select a form", { position: "top-center", duration: 2500 });
          return false;
        }
        if (this.reportQuery.from && this.reportQuery.to && this.reportQuery.from > this.reportQuery.to) {
          this.$toasted.error("'From' date must be before 'To' date", { position: "top-center", duration: 2500 });
          return false;
        }
        return true;
      },
      async generateReport() {
        if (!this.validateReportQuery()) return;
        this.reportLoading = true;
        try {
          const { data } = await this.axios.post("/api/v1/auth/qa-dashboard/aggregate-report", this.buildReportPayload("json"));
          this.report = data;
          this.$nextTick(() => {
            if (this.$refs.reportResults) {
              this.$refs.reportResults.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        } catch (error) {
          console.error(error);
          const message = (error.response && error.response.data && error.response.data.message) || "Failed to generate report";
          this.$toasted.error(message, { position: "top-center", duration: 3500 });
        } finally {
          this.reportLoading = false;
        }
      },
      async downloadReportExcel() {
        if (!this.validateReportQuery()) return;
        this.reportLoading = true;
        try {
          const response = await this.axios.post("/api/v1/auth/qa-dashboard/aggregate-report",
            this.buildReportPayload("xlsx"), { responseType: "blob" });
          const contentDisposition = response.headers["content-disposition"] || "";
          const matched = contentDisposition.match(/filename="?([^"]+)"?/i);
          const filename = matched && matched[1] ? matched[1] : "qa-aggregate-report.xlsx";
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error(error);
          let message = "Failed to download report";
          // Errors come back as a Blob because of responseType: "blob".
          if (error.response && error.response.data instanceof Blob) {
            try {
              const body = JSON.parse(await error.response.data.text());
              message = body.message || message;
            } catch (e) { /* not JSON */ }
          }
          this.$toasted.error(message, { position: "top-center", duration: 3500 });
        } finally {
          this.reportLoading = false;
        }
      },
      onReportLgaChange() {
        // Drop selected facilities that are not in the newly selected LGA.
        const allowed = new Set(this.reportProviders.map((p) => p.id));
        this.reportQuery.provider_ids = (this.reportQuery.provider_ids || []).filter((id) => allowed.has(id));
      },
      async fetchReportProviders() {
        if (!this.query.agency_id) { return; }
        try {
          const { data } = await this.axios.get(`/api/v1/auth/providerAgency/${this.query.agency_id}`);
          this.reportProviderList = Array.isArray(data.data) ? data.data : [];
        } catch (error) { console.error(error); }
      },
      fetchLga() {
        const stateId = (this.user && this.user.state) || 2679;
        this.axios
          .get(`/api/v1/auth/lga/${stateId}`)
          .then((response) => { this.lga_states = (response.data && response.data.data) || []; })
          .catch((error) => { console.error(error); });
      },
    },
    async created() {
      this.fetchLga();
      this.fetchReportProviders();
      try {
        await this.fetchForms();
        await this.fetchProviders();
        await this.fetchMeta();
        if (this.query.form_id) {
          await this.fetchAnalytics(false);
        } else {
          this.fetchEncounterStats();
          this.fetchClaimsStats();
          this.fetchReferralStats();
        }
      } catch (error) {
        console.error(error);
        this.$toasted.error("Failed to initialize dashboard", { position: "top-center", duration: 3000 });
      }
    },
  };
</script>

<style scoped>
  /* ── Page Header ── */
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 40px;
  }

  .header-icon-wrap {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .filter-toggle-btn {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.85rem;
    padding: 7px 16px;
    transition: background 0.15s;
  }

  .filter-toggle-btn.active {
    background: rgba(255, 255, 255, 0.3);
  }

  .filter-toggle-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
  }

  /* ── Card Shells ── */
  .form-card,
  .table-card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    overflow: hidden;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }

  .section-number {
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;
  }

  /* ── Filter Card ── */
  .filter-label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    margin-bottom: 4px;
    display: block;
  }

  .filter-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0 12px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }

  .filter-divider::before,
  .filter-divider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  /* Header action buttons (inside form-card-header) */
  .btn-header-action {
    padding: 5px 12px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    display: inline-flex;
    align-items: center;
  }

  .btn-header-action:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-header-action:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  .btn-header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 0.78rem;
    cursor: pointer;
    transition: background 0.15s;
  }

  .btn-header-icon:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  /* ── Submit Button ── */
  .btn-submit {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    border: none;
    color: #fff;
    border-radius: 8px;
    font-weight: 600;
    padding: 8px 20px;
  }

  .btn-submit:hover {
    opacity: 0.9;
    color: #fff;
  }

  /* ── Stat Cards ── */
  .stat-card {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 18px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 16px;
    min-height: 100px;
  }

  .stat-icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .stat-body {
    flex: 1;
    min-width: 0;
  }

  .stat-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin-bottom: 4px;
    font-weight: 600;
  }

  .stat-value {
    font-size: 1.15rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
    line-height: 1.2;
    word-break: break-word;
  }

  .stat-link {
    font-size: 0.72rem;
    font-weight: 600;
    color: #2563eb;
    text-decoration: none;
  }

  .stat-link:hover {
    text-decoration: underline;
  }

  /* ── Non-submitters Panel ── */
  .non-submit-card {
    border: 1px solid #e9ecef;
    border-left: 4px solid #f59e0b;
    border-radius: 12px;
    overflow: hidden;
  }

  .non-submit-header {
    background: #fff;
    border-bottom: 1px solid #f3f4f6;
    padding: 14px 20px;
  }

  .facility-chip {
    font-size: 0.8rem;
    font-weight: 500;
    border-radius: 6px;
    border: 1px solid #f59e0b;
    background: #fffbeb;
    color: #92400e;
    padding: 4px 10px;
    display: inline-block;
  }

  /* ── Bar Charts ── */
  .bar-row {
    display: grid;
    grid-template-columns: 1.6fr 3fr auto;
    gap: 10px;
    align-items: center;
    margin-bottom: 8px;
  }

  .bar-label {
    font-size: 0.82rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #334155;
  }

  .bar-label--full {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-break: break-word;
    line-height: 1.3;
  }

  .bar-track {
    width: 100%;
    height: 10px;
    border-radius: 8px;
    background: #e9edf3;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    border-radius: 8px;
  }

  .bar-value {
    font-size: 0.78rem;
    font-weight: 600;
    color: #475569;
  }

  /* ── Aggregate Report ── */
  .report-table-wrap {
    overflow: auto;
    max-height: 560px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
  }

  .report-table {
    font-size: 0.8rem;
    border-collapse: separate;
    border-spacing: 0;
  }

  .report-table th,
  .report-table td {
    white-space: nowrap;
    vertical-align: middle;
    background: #fff;
  }

  .report-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc;
    color: #334155;
    font-weight: 700;
  }

  .report-table thead th.item-col {
    white-space: normal;
    min-width: 170px;
    max-width: 220px;
    font-weight: 600;
    font-size: 0.74rem;
    line-height: 1.3;
  }

  .report-table tfoot th {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: #eef2ff;
    color: #1e3a5f;
  }

  .report-table .sticky-col {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 110px;
  }

  .report-table .sticky-col-2 {
    left: 110px;
    min-width: 140px;
  }

  .report-table thead .sticky-col,
  .report-table tfoot .sticky-col {
    z-index: 3;
  }

  .report-table tbody tr:hover td {
    background: #f8fafc;
  }

  /* ── Empty State ── */
  .empty-state {
    text-align: center;
    padding: 32px 0;
    color: #94a3b8;
  }

  .empty-state i {
    font-size: 2rem;
    opacity: 0.35;
    display: block;
    margin-bottom: 8px;
  }

  .empty-state p {
    font-size: 0.85rem;
    margin: 0;
  }
</style>